import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Form, FormGroup,
  Input, Label, Row,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useMtffApiBindings,
  useMtffInstance,
  useMtffWordpressSource,
  useSaveApiBindings,
  useSaveWordpressSource,
  useTestWordpressSource,
} from './useMtff';

/**
 * MTFF Sources & API-Bindings pro Instance.
 *
 * Spec:
 *  - MTFF/docs/ORBIT-INTEGRATION.md §3.4 (WordPress-Source)
 *  - 02_applications/07_MTFF/README.md §9.1 (API Bindings)
 *
 * API-Bindings (CORE/NEXUS/Pulse) wandern aktuell noch durch das
 * core-auth-Plugin-Settings-Schema — wenn MTFF einen eigenen
 * /api-bindings-Endpoint nachzieht, ist nur die Hook-Mapping-Stelle
 * (useMtffApiBindings) zu tauschen.
 */

const WP_AUTH_MODES = [
  { value: 'basic_auth', label: 'Basic Auth (Username + Password)' },
  { value: 'jwt', label: 'JWT Auth Plugin' },
  { value: 'application_password', label: 'Application Password (empfohlen)' },
];

export function MtffSourcesPage() {
  const { instanceId } = useParams();
  const enabled = isMtffProxyEnabled();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const instance = useMtffInstance(instanceId);

  if (!enabled) {
    return (
      <div>
        <PageHeader title="MTFF · Sources" />
        <Alert color="warning" className="small">MTFF-Admin-Proxy nicht konfiguriert.</Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`MTFF · Sources · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle="WordPress-Source + API-Bindings (CORE/NEXUS/Pulse)"
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />
      <Row className="g-3">
        <Col lg="6">
          <WordpressSourceCard instanceId={instanceId} canWrite={canWrite} />
        </Col>
        <Col lg="6">
          <ApiBindingsCard instanceId={instanceId} canWrite={canWrite} />
        </Col>
      </Row>
    </div>
  );
}

/* ── WordPress-Source ──────────────────────────────────────────────── */

function WordpressSourceCard({ instanceId, canWrite }) {
  const query = useMtffWordpressSource(instanceId);

  if (!query.data) {
    return (
      <Card><CardBody>
        <h6 className="text-uppercase small text-muted mb-2">WordPress-Source</h6>
        <p className="small text-muted mb-0">Lade…</p>
      </CardBody></Card>
    );
  }
  return (
    <WordpressSourceForm
      key={`${instanceId}-${query.data.baseUrl ?? ''}`}
      instanceId={instanceId}
      initial={query.data}
      canWrite={canWrite}
    />
  );
}

function WordpressSourceForm({ instanceId, initial, canWrite }) {
  const save = useSaveWordpressSource();
  const test = useTestWordpressSource();
  const [form, setForm] = useState(() => ({
    baseUrl: initial.baseUrl ?? '',
    authMode: initial.authMode ?? 'application_password',
    basicAuthUser: '',
    basicAuthPassword: '',
    jwt: '',
    applicationPassword: '',
    webhookSecret: '',
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const body = {
      baseUrl: form.baseUrl.trim(),
      authMode: form.authMode,
    };
    if (form.authMode === 'basic_auth' && (form.basicAuthUser || form.basicAuthPassword)) {
      body.basicAuth = { username: form.basicAuthUser, password: form.basicAuthPassword };
    }
    if (form.authMode === 'jwt' && form.jwt) body.jwt = form.jwt;
    if (form.authMode === 'application_password' && form.applicationPassword) {
      body.applicationPassword = form.applicationPassword;
    }
    if (form.webhookSecret) body.webhookSecret = form.webhookSecret;
    await save.mutateAsync({ instanceId, ...body });
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="text-uppercase small text-muted mb-0">WordPress-Source</h6>
          {initial.hasWebhookSecret && (
            <Badge color="success" pill className="orbit-code-badge-sm">webhook-secret gesetzt</Badge>
          )}
        </div>
        <Form onSubmit={onSubmit}>
          <FormGroup>
            <Label className="small">Base-URL</Label>
            <Input
              type="url"
              bsSize="sm"
              value={form.baseUrl}
              onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
              placeholder="https://wp.example.com"
              disabled={!canWrite}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Auth-Mode</Label>
            <Input
              type="select" bsSize="sm" value={form.authMode}
              onChange={(e) => setForm((f) => ({ ...f, authMode: e.target.value }))}
              disabled={!canWrite}
            >
              {WP_AUTH_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Input>
          </FormGroup>
          {form.authMode === 'basic_auth' && (
            <>
              <FormGroup>
                <Label className="small">Username</Label>
                <Input
                  type="text" bsSize="sm" value={form.basicAuthUser}
                  onChange={(e) => setForm((f) => ({ ...f, basicAuthUser: e.target.value }))}
                  disabled={!canWrite}
                />
              </FormGroup>
              <FormGroup>
                <Label className="small">Password</Label>
                <Input
                  type="password" bsSize="sm" value={form.basicAuthPassword}
                  onChange={(e) => setForm((f) => ({ ...f, basicAuthPassword: e.target.value }))}
                  placeholder="Leer lassen wenn unverändert"
                  disabled={!canWrite}
                />
              </FormGroup>
            </>
          )}
          {form.authMode === 'jwt' && (
            <FormGroup>
              <Label className="small">JWT</Label>
              <Input
                type="password" bsSize="sm" value={form.jwt}
                onChange={(e) => setForm((f) => ({ ...f, jwt: e.target.value }))}
                placeholder="Leer lassen wenn unverändert"
                disabled={!canWrite}
              />
            </FormGroup>
          )}
          {form.authMode === 'application_password' && (
            <FormGroup>
              <Label className="small">Application-Password</Label>
              <Input
                type="password" bsSize="sm" value={form.applicationPassword}
                onChange={(e) => setForm((f) => ({ ...f, applicationPassword: e.target.value }))}
                placeholder="Leer lassen wenn unverändert"
                disabled={!canWrite}
              />
              <small className="text-muted">
                In WP unter „Benutzer · Profil · Application Passwords" erzeugen.
              </small>
            </FormGroup>
          )}
          <FormGroup>
            <Label className="small">Webhook-Secret (für Revalidate-Hooks)</Label>
            <Input
              type="password" bsSize="sm" value={form.webhookSecret}
              onChange={(e) => setForm((f) => ({ ...f, webhookSecret: e.target.value }))}
              placeholder="Leer lassen wenn unverändert"
              disabled={!canWrite}
            />
          </FormGroup>
          <HasPermission perms={['integrations.write']}>
            <ButtonGroup size="sm" className="mt-2">
              <Button color="primary" type="submit" disabled={!canWrite || save.isPending}>
                {save.isPending ? 'Speichere…' : '💾 Speichern'}
              </Button>
              <Button
                color="info" outline type="button"
                onClick={() => test.mutate({ instanceId })}
                disabled={!canWrite || test.isPending}
              >
                {test.isPending ? 'Teste…' : '🔌 Connection-Test'}
              </Button>
            </ButtonGroup>
          </HasPermission>
        </Form>
      </CardBody>
    </Card>
  );
}

/* ── API-Bindings ──────────────────────────────────────────────────── */

function ApiBindingsCard({ instanceId, canWrite }) {
  const query = useMtffApiBindings(instanceId);
  if (!query.data) {
    return (
      <Card><CardBody>
        <h6 className="text-uppercase small text-muted mb-2">API-Bindings</h6>
        <p className="small text-muted mb-0">Lade…</p>
      </CardBody></Card>
    );
  }
  return (
    <ApiBindingsForm
      key={`${instanceId}-bindings`}
      instanceId={instanceId}
      initial={query.data.settings ?? {}}
      canWrite={canWrite}
    />
  );
}

function ApiBindingsForm({ instanceId, initial, canWrite }) {
  const save = useSaveApiBindings();
  const [form, setForm] = useState(() => ({
    coreUrl: initial.coreUrl ?? '',
    coreClientId: initial.coreClientId ?? '',
    coreClientSecret: '',
    nexusUrl: initial.nexusUrl ?? '',
    nexusToken: '',
    pulseUrl: initial.pulseUrl ?? '',
    pulseToken: '',
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    // Replace-Semantik: Plugin-Settings sind PUT. Wir nehmen das geladene
    // Schema und überschreiben nur die Bindings-Felder; secrets nur wenn
    // im Form gesetzt (Backend interpretiert leer als „unverändert" — Spec
    // sagt das für sekrete-Felder zu).
    const settings = { ...initial };
    settings.coreUrl = form.coreUrl.trim();
    settings.coreClientId = form.coreClientId.trim();
    if (form.coreClientSecret) settings.coreClientSecret = form.coreClientSecret;
    settings.nexusUrl = form.nexusUrl.trim();
    if (form.nexusToken) settings.nexusToken = form.nexusToken;
    settings.pulseUrl = form.pulseUrl.trim();
    if (form.pulseToken) settings.pulseToken = form.pulseToken;
    await save.mutateAsync({ instanceId, settings });
  };

  return (
    <Card>
      <CardBody>
        <h6 className="text-uppercase small text-muted mb-2">API-Bindings</h6>
        <p className="small text-muted">
          Endpoints + Credentials für CORE, NEXUS und Pulse. Sekrete (Secret-
          oder Token-Felder) bleiben leer wenn sie unverändert übernommen
          werden sollen — MTFF behält den alten Wert.
        </p>
        <Form onSubmit={onSubmit}>
          <h6 className="small text-uppercase text-muted mt-2">CORE</h6>
          <FormGroup>
            <Label className="small">Base-URL</Label>
            <Input
              type="url" bsSize="sm" value={form.coreUrl}
              onChange={(e) => setForm((f) => ({ ...f, coreUrl: e.target.value }))}
              placeholder="https://core.api.valitype.net"
              disabled={!canWrite}
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Client-ID</Label>
            <Input
              type="text" bsSize="sm" value={form.coreClientId}
              onChange={(e) => setForm((f) => ({ ...f, coreClientId: e.target.value }))}
              disabled={!canWrite}
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Client-Secret</Label>
            <Input
              type="password" bsSize="sm" value={form.coreClientSecret}
              onChange={(e) => setForm((f) => ({ ...f, coreClientSecret: e.target.value }))}
              placeholder="Leer lassen wenn unverändert"
              disabled={!canWrite}
            />
          </FormGroup>

          <h6 className="small text-uppercase text-muted mt-2">NEXUS</h6>
          <FormGroup>
            <Label className="small">Base-URL</Label>
            <Input
              type="url" bsSize="sm" value={form.nexusUrl}
              onChange={(e) => setForm((f) => ({ ...f, nexusUrl: e.target.value }))}
              placeholder="https://nexus.api.valitype.net"
              disabled={!canWrite}
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">M2M-Token</Label>
            <Input
              type="password" bsSize="sm" value={form.nexusToken}
              onChange={(e) => setForm((f) => ({ ...f, nexusToken: e.target.value }))}
              placeholder="Leer lassen wenn unverändert"
              disabled={!canWrite}
            />
          </FormGroup>

          <h6 className="small text-uppercase text-muted mt-2">Pulse</h6>
          <FormGroup>
            <Label className="small">Base-URL</Label>
            <Input
              type="url" bsSize="sm" value={form.pulseUrl}
              onChange={(e) => setForm((f) => ({ ...f, pulseUrl: e.target.value }))}
              placeholder="https://pulse.api.valitype.net"
              disabled={!canWrite}
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Token</Label>
            <Input
              type="password" bsSize="sm" value={form.pulseToken}
              onChange={(e) => setForm((f) => ({ ...f, pulseToken: e.target.value }))}
              placeholder="Leer lassen wenn unverändert"
              disabled={!canWrite}
            />
          </FormGroup>

          <HasPermission perms={['integrations.write']}>
            <Button color="primary" type="submit" size="sm" disabled={!canWrite || save.isPending}>
              {save.isPending ? 'Speichere…' : '💾 Bindings speichern'}
            </Button>
          </HasPermission>
        </Form>
      </CardBody>
    </Card>
  );
}
